/* ==========================================================================
   Prescription Form Page
   PORTAL-26: supports multiple product sections per submission. Patient,
   address, payment/delivery, diagnosis, and signature are shared across all
   products. Each <ProductSection> is fully isolated (own RHF instance).
   ========================================================================== */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { PageContainer } from '@/components/layout/PageContainer'
import {
  PatientForm,
  patientFormDefaultValues,
} from '@/components/forms/PatientForm'
import { SignatureCapture } from '@/components/forms/SignatureCapture'
import { ProviderTabBar } from '@/components/layout/ProviderTabBar'
import { ProviderPageHeader } from '@/components/layout/ProviderPageHeader'
import {
  submitPrescriptionBulkChunked,
  BulkSubmitPartialError,
  saveDraft,
  updateDraft,
  deleteDraft,
} from '@/api/portal'
import { ApiClientError } from '@/api/client'
import { formatPracticeAddressLine } from '@/utils/address'
import { providerPayLabel } from '@/utils/paymentLabels'
import {
  DEFAULT_COUNTRY,
  formatPostalCode,
  isUSStateCountry,
  validatePostalCode,
} from '@/utils/countries'
import { usePortalSlug, usePortal } from '@/context/PortalContext'
import { useAuthContext } from '@/context/AuthContext'
import { useToast } from '@/context/ToastContext'
import { DiagnosisCodeSearch } from '@/components/forms/DiagnosisCodeSearch'
import { PatientSelector } from '@/components/forms/PatientSelector'
import {
  ProductSection,
  type ProductSectionHandle,
} from '@/components/forms/ProductSection'
import { ChevronDown, ChevronUp, User, Loader2, PenLine, CreditCard, MapPin, Plus, Save, ClipboardList } from 'lucide-react'
import { CustomFieldsEdit } from '@/components/forms/CustomFieldsEdit'
import {
  compactCustomFieldValues,
  getMissingRequiredCustomFields,
  pickDefinedCustomFieldValues,
} from '@/utils/customFields'
import type {
  PatientSummary,
  PrescriptionPatientPayload,
  PrescriptionLineItem,
  PaymentType,
  DeliveryMethod,
  ProviderAddressResponse,
  DraftLineItem,
  PrescriptionDraftSave,
  BulkPrescriptionResult,
} from '@/api/types'
import { MAX_PRODUCTS_PER_SUBMISSION } from '@/api/types'
import type { PatientFormData } from '@/components/forms/PatientForm'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

/**
 * Patient fields plus the pharmacy's general custom field answers (PORTAL-164).
 * General fields are order-level, so they live on the page form rather than
 * inside a product subform.
 */
type PrescriptionFormData = PatientFormData & {
  general_custom_field_values?: Record<string, string | number | boolean>
}

const DELIVERY_METHOD_LABELS: Record<DeliveryMethod, string> = {
  patient_pickup: 'Patient Pickup',
  patient_ship: 'Patient Ship',
  provider_pickup: 'Provider Pickup',
  provider_ship: 'Provider Ship',
}

const DELIVERY_METHOD_OPTIONS = (
  Object.keys(DELIVERY_METHOD_LABELS) as DeliveryMethod[]
).map((value) => ({ value, label: DELIVERY_METHOD_LABELS[value] }))

/**
 * Whether the selected practice address still offers `value` (PORTAL-84):
 * an empty list means the pharmacy configured nothing, otherwise the value
 * has to be one of the configured options.
 */
function isAllowedOption<T extends string>(value: T, allowed: readonly T[]): boolean {
  return allowed.length === 0 || allowed.includes(value)
}

// Stable "nothing configured" list so effects keyed on the allowed options do
// not re-run every render when no practice address is selected.
const NO_OPTIONS: never[] = []

/* --------------------------------------------------------------------------
   Section Header Component (shared sections only)
   -------------------------------------------------------------------------- */

interface SectionHeaderProps {
  title: string
  icon: React.ReactNode
  isCollapsible?: boolean
  isCollapsed?: boolean
  isComplete?: boolean
  onToggle?: () => void
}

function SectionHeader({
  title,
  icon,
  isCollapsible = false,
  isCollapsed = false,
  isComplete = false,
  onToggle,
}: SectionHeaderProps) {
  return (
    <div
      className={`flex items-center justify-between ${isCollapsible ? 'cursor-pointer' : ''}`}
      onClick={isCollapsible ? onToggle : undefined}
      role={isCollapsible ? 'button' : undefined}
      tabIndex={isCollapsible ? 0 : undefined}
      onKeyDown={
        isCollapsible
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onToggle?.()
              }
            }
          : undefined
      }
    >
      <div className="flex items-center gap-3">
        <div
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
            isComplete
              ? 'bg-[var(--color-success-light)] text-[var(--color-success)]'
              : 'bg-[var(--color-soft-lavender)] text-[var(--pharmacy-primary)]'
          }`}
        >
          {icon}
        </div>
        <h2 className="text-lg font-semibold text-[var(--color-charcoal)]">
          {title}
        </h2>
        {isComplete && (
          <span className="text-xs font-medium text-[var(--color-success)] bg-[var(--color-success-light)] px-2 py-0.5 rounded-full">
            Complete
          </span>
        )}
      </div>
      {isCollapsible && (
        <div className="text-[var(--color-gray-500)]">
          {isCollapsed ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronUp className="h-5 w-5" />
          )}
        </div>
      )}
    </div>
  )
}

/* --------------------------------------------------------------------------
   Prefill State Type (from PrescribeAgainModal — single-product flow only)
   -------------------------------------------------------------------------- */

interface PrefillState {
  samePatient: boolean
  catalogProductId: string | null
  drugName: string
  patientType: 'human' | 'animal'
  patientFirstName: string | null
  patientLastName: string | null
  patientMiddleName: string | null
  patientDob: string | null
  patientGender: 'male' | 'female' | 'nonbinary' | 'unknown' | null
  patientPhone: string | null
  patientEmail: string | null
  patientAddress: { street: string; city: string; state: string; zip: string } | null
  patientAllergies: string | null
  noKnownAllergies?: boolean
  currentMedications?: string | null
  noCurrentMedications?: boolean
  animalName: string | null
  animalSpecies: string | null
  animalBreed: string | null
  ownerName: string | null
  ownerPhone: string | null
  patientWeight: string | null
  patientWeightUnit: string | null
  animalDobUnknown?: boolean
  doseSpecificDirections: string
  quantity: string
  unitAmount: string
  daysSupply: number
  refills: number
  customFieldValues?: Record<string, string | number | boolean> | null
  generalCustomFieldValues?: Record<string, string | number | boolean> | null
  diagnosisCode?: string | null
  paymentType?: string | null
  deliveryMethod?: string | null
}

/* --------------------------------------------------------------------------
   Draft prefill (PORTAL-61 — "save script for later", multi-product)
   -------------------------------------------------------------------------- */

interface DraftPrefillState {
  draftId: string
  payload: PrescriptionDraftSave
}

/** Per-section seed for the product subforms (covers both prefill sources). */
interface SectionSeed {
  id: string
  productId?: string
  customFieldValues?: Record<string, string | number | boolean>
  values?: {
    dose_specific_directions?: string | null
    quantity?: string | null
    unit_amount?: string | null
    days_supply?: number | null
    refills?: number | null
  }
}

/**
 * The API stores the pet owner's name as a single string; the form edits it as
 * first + last (PORTAL-157). Split on the first space so multi-word last names
 * survive a round-trip ("Mary Jane Smith" → "Mary" / "Jane Smith").
 */
function splitOwnerName(full: string | null | undefined) {
  const t = (full ?? '').trim()
  const i = t.indexOf(' ')
  return i === -1 ? { first: t, last: '' } : { first: t.slice(0, i), last: t.slice(i + 1) }
}

/**
 * Compute the initial product sections from navigation state. Two sources are
 * supported and kept separate to avoid regressing the existing single-product
 * deep link:
 *  - `draftPrefill`: a saved multi-product draft — one seeded section per line
 *    item, carrying full saved values so the form restores exactly.
 *  - `prefill`: the existing single-product "prescribe again" link — one
 *    section seeded with the product + custom fields. For the same patient
 *    the whole script (direction, quantity, unit, days supply, refills) is
 *    copied so the prescriber only has to submit; for a different patient
 *    details fall back to catalog defaults (PORTAL-153).
 */
function buildInitialSeeds(state: unknown, newId: () => string): SectionSeed[] {
  const st = state as
    | { prefill?: PrefillState; draftPrefill?: DraftPrefillState }
    | null

  if (st?.draftPrefill) {
    const items = st.draftPrefill.payload.prescriptions ?? []
    if (items.length > 0) {
      return items.map((li) => ({
        id: newId(),
        productId: li.catalog_product_id || undefined,
        customFieldValues:
          (li.custom_field_values as Record<string, string | number | boolean>) ??
          undefined,
        // Pass draft values through unchanged so an explicitly-cleared field
        // (null) survives restore instead of collapsing to undefined and
        // reverting to the catalog default.
        values: {
          dose_specific_directions: li.dose_specific_directions,
          quantity: li.quantity,
          unit_amount: li.unit_amount,
          days_supply: li.days_supply,
          refills: li.refills,
        },
      }))
    }
  }

  if (st?.prefill) {
    return [
      {
        id: newId(),
        productId: st.prefill.catalogProductId ?? undefined,
        customFieldValues: st.prefill.customFieldValues ?? undefined,
        // Same patient: copy the whole script so it's submit-ready.
        // Different patient: product only — direction/quantity fall back to
        // catalog defaults for the prescriber to fill in (PORTAL-153).
        values: st.prefill.samePatient
          ? {
              dose_specific_directions: st.prefill.doseSpecificDirections,
              quantity: st.prefill.quantity,
              unit_amount: st.prefill.unitAmount,
              days_supply: st.prefill.daysSupply,
              refills: st.prefill.refills,
            }
          : undefined,
      },
    ]
  }

  return [{ id: newId() }]
}

/**
 * General custom field answers to restore on mount (PORTAL-164): from a saved
 * draft, or from the prescribe-again link when it is for the same patient.
 */
function initialGeneralCustomFieldValues(
  state: unknown,
): Record<string, string | number | boolean> {
  const st = state as
    | { prefill?: PrefillState; draftPrefill?: DraftPrefillState }
    | null
  const fromDraft = st?.draftPrefill?.payload.general_custom_field_values
  if (fromDraft) return { ...fromDraft }
  if (st?.prefill?.samePatient && st.prefill.generalCustomFieldValues) {
    return { ...st.prefill.generalCustomFieldValues }
  }
  return {}
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

/**
 * Patient postal code as sent to the API (PORTAL-161).
 * - US keeps the pre-existing digits-only payload (e.g. "941031234"), so
 *   stored prescriptions and the faxed PDF are unchanged for US pharmacies.
 * - CA normalizes a valid postal code to "A1A 1A1"; anything that is not a
 *   valid Canadian postal code (e.g. a saved patient with a US address) is
 *   passed through untouched rather than mangled.
 */
function buildPatientZip(country: string, zip: string): string {
  if (isUSStateCountry(country)) return zip.replace(/\D/g, '')
  const trimmed = zip.trim()
  if (!trimmed) return ''
  return validatePostalCode(country, trimmed) === null
    ? formatPostalCode(country, trimmed)
    : trimmed
}

export function PrescriptionForm() {
  const slug = usePortalSlug()
  const { config } = usePortal()
  // Pharmacy country (PORTAL-161): drives patient-address labels and the
  // postal-code format, and is stamped on the submitted patient address.
  const pharmacyCountry = config?.country ?? DEFAULT_COUNTRY
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const location = useLocation()

  const { error: showError, success: showSuccess } = useToast()

  // Determine portal type and default patient type
  const portalType = config?.portal_type ?? 'human'
  const defaultPatientType = portalType === 'veterinary' ? 'animal' : 'human'

  // Patient-only form state (product fields live inside each ProductSection)
  const methods = useForm<PrescriptionFormData>({
    defaultValues: {
      ...patientFormDefaultValues,
      patient_type: defaultPatientType as 'human' | 'animal',
      general_custom_field_values: initialGeneralCustomFieldValues(location.state),
    },
    mode: 'onSubmit',
  })

  const {
    handleSubmit,
    watch,
    formState: { isSubmitting, errors },
  } = methods

  // Multi-product section bookkeeping
  const newSectionId = () =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `section-${Date.now()}-${Math.random().toString(36).slice(2)}`

  // Seeds for the initial product section(s). Single source of truth for both
  // the prescribe-again deep link and a saved multi-product draft restore.
  const [initialSeeds] = useState<SectionSeed[]>(() =>
    buildInitialSeeds(location.state, newSectionId)
  )
  const seedById = useMemo(() => {
    const map: Record<string, SectionSeed> = {}
    for (const seed of initialSeeds) map[seed.id] = seed
    return map
  }, [initialSeeds])
  // When continuing a saved draft, remember its id so Save updates it in place
  // and a successful submit removes it from the queue.
  const [draftId] = useState<string | null>(
    () =>
      (location.state as { draftPrefill?: DraftPrefillState } | null)?.draftPrefill
        ?.draftId ?? null
  )
  // The draft's signature is captured ONCE here (a mount-time value), not from
  // the live `providerSignature`, so SignatureCapture can show a "Signed" state
  // on restore without re-painting the canvas. Using the live value would wrongly
  // flip the canvas into the signed state right after a freshly-drawn signature.
  const [initialDraftSignature] = useState<string | null>(
    () =>
      (location.state as { draftPrefill?: DraftPrefillState } | null)?.draftPrefill
        ?.payload?.signature ?? null
  )

  const [sectionIds, setSectionIds] = useState<string[]>(() =>
    initialSeeds.map((s) => s.id)
  )
  const sectionRefs = useRef<Record<string, ProductSectionHandle | null>>({})
  const [readyMap, setReadyMap] = useState<Record<string, boolean>>({})
  const [savingDraft, setSavingDraft] = useState(false)

  const [patientSectionCollapsed, setPatientSectionCollapsed] = useState(false)
  const [providerSignature, setProviderSignature] = useState<string | null>(null)
  const [signatureError, setSignatureError] = useState<string | undefined>()
  const [diagnosisCode, setDiagnosisCode] = useState<string>('')

  // Provider practice addresses (issues #140/#180). The selected id is sent
  // with the prescription; backend snapshots into the PDF.
  const providerAddresses: ProviderAddressResponse[] = user?.addresses ?? []
  const primaryAddress =
    providerAddresses.find((a) => a.is_primary) ?? providerAddresses[0] ?? null
  const [selectedAddressId, setSelectedAddressId] = useState<string>(
    primaryAddress?.id ?? ''
  )
  useEffect(() => {
    if (!selectedAddressId && primaryAddress) {
      setSelectedAddressId(primaryAddress.id)
    }
  }, [primaryAddress, selectedAddressId])

  // Pharmacy-configured payment / delivery options come from the currently
  // selected practice address (issue #192, PORTAL-84). An empty list leaves
  // the full choice, exactly one value locks the field, and several limit
  // the radios to those options.
  const selectedAddress =
    providerAddresses.find((a) => a.id === selectedAddressId) ?? null
  const allowedPaymentTypes = selectedAddress?.default_payment_types ?? NO_OPTIONS
  const allowedDeliveryMethods = selectedAddress?.default_delivery_methods ?? NO_OPTIONS
  const lockedPaymentType =
    allowedPaymentTypes.length === 1 ? allowedPaymentTypes[0] : null
  const lockedDeliveryMethod =
    allowedDeliveryMethods.length === 1 ? allowedDeliveryMethods[0] : null

  const [paymentType, setPaymentType] = useState<PaymentType | ''>(
    lockedPaymentType ?? ''
  )
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod | ''>(
    lockedDeliveryMethod ?? ''
  )

  useEffect(() => {
    if (lockedPaymentType) setPaymentType(lockedPaymentType)
  }, [lockedPaymentType])
  useEffect(() => {
    if (lockedDeliveryMethod) setDeliveryMethod(lockedDeliveryMethod)
  }, [lockedDeliveryMethod])

  // When the selected address limits the options, drop a selection that is
  // no longer offered (e.g. after switching "Prescribing From").
  useEffect(() => {
    if (
      allowedPaymentTypes.length >= 2 &&
      paymentType &&
      !allowedPaymentTypes.includes(paymentType)
    ) {
      setPaymentType('')
    }
  }, [allowedPaymentTypes, paymentType])
  useEffect(() => {
    if (
      allowedDeliveryMethods.length >= 2 &&
      deliveryMethod &&
      !allowedDeliveryMethods.includes(deliveryMethod)
    ) {
      setDeliveryMethod('')
    }
  }, [allowedDeliveryMethods, deliveryMethod])

  // Restore a saved draft (PORTAL-61). Products are seeded per-section via
  // `initialSeeds`; here we restore the shared fields: patient, diagnosis,
  // payment/delivery, practice address, and the signature (if the user signed
  // before saving). Runs once on mount.
  useEffect(() => {
    const state = location.state as { draftPrefill?: DraftPrefillState } | null
    const draft = state?.draftPrefill
    if (!draft) return
    window.history.replaceState({}, '')

    const p = draft.payload
    const patient = p.patient
    if (patient) {
      const ptype = (patient.patient_type as 'human' | 'animal') ?? defaultPatientType
      methods.setValue('patient_type', ptype)
      methods.setValue('no_known_allergies', patient.no_known_allergies ?? false)
      methods.setValue('current_medications', patient.current_medications ?? '')
      methods.setValue('no_current_medications', patient.no_current_medications ?? false)
      methods.setValue('patient_allergies', patient.patient_allergies ?? '')
      methods.setValue('patient_gender', patient.patient_gender ?? 'unknown')
      methods.setValue('patient_email', patient.patient_email ?? '')

      if (ptype === 'human') {
        methods.setValue('patient_first_name', patient.patient_first_name ?? '')
        methods.setValue('patient_last_name', patient.patient_last_name ?? '')
        methods.setValue('patient_middle_name', patient.patient_middle_name ?? '')
        methods.setValue('patient_dob', patient.patient_dob ?? '')
        methods.setValue('patient_phone', patient.patient_phone ?? '')
        if (patient.patient_address) {
          methods.setValue('patient_address_street', patient.patient_address.street ?? '')
          methods.setValue('patient_address_city', patient.patient_address.city ?? '')
          methods.setValue('patient_address_state', patient.patient_address.state ?? '')
          methods.setValue('patient_address_zip', patient.patient_address.zip ?? '')
        }
      } else {
        methods.setValue('animal_name', patient.animal_name ?? '')
        methods.setValue('animal_species', patient.animal_species ?? '')
        methods.setValue('animal_breed', patient.animal_breed ?? '')
        methods.setValue('patient_dob', patient.patient_dob ?? '')
        methods.setValue('animal_dob_unknown', patient.animal_dob_unknown ?? false)
        const ownerParts = splitOwnerName(patient.owner_name)
        methods.setValue('owner_first_name', ownerParts.first)
        methods.setValue('owner_last_name', ownerParts.last)
        methods.setValue('owner_phone', patient.owner_phone ?? '')
        methods.setValue('patient_weight', patient.patient_weight ?? '')
        methods.setValue('patient_weight_unit', patient.patient_weight_unit ?? 'lbs')
      }

      if (patient.diagnosis_code) setDiagnosisCode(patient.diagnosis_code)

      // Restore payment/delivery against the SAVED address's options, not the
      // primary's — otherwise a saved choice is dropped when the primary address
      // has a locked default and the saved address doesn't. A value the saved
      // address no longer offers is skipped. (The lockedPaymentType sync effect
      // still enforces a lock when the saved address has one.)
      const savedAddr = p.practice_address_id
        ? providerAddresses.find((a) => a.id === p.practice_address_id)
        : null
      const savedPaymentOptions = savedAddr?.default_payment_types ?? []
      const savedDeliveryOptions = savedAddr?.default_delivery_methods ?? []
      if (
        patient.payment_type &&
        isAllowedOption(patient.payment_type as PaymentType, savedPaymentOptions)
      ) {
        setPaymentType(patient.payment_type as PaymentType)
      }
      if (
        patient.delivery_method &&
        isAllowedOption(patient.delivery_method as DeliveryMethod, savedDeliveryOptions)
      ) {
        setDeliveryMethod(patient.delivery_method as DeliveryMethod)
      }
    }

    if (p.practice_address_id) setSelectedAddressId(p.practice_address_id)
    if (p.signature) setProviderSignature(p.signature)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle pre-fill from prescribe-again flow (single-product). Products are
  // seeded per-section via `initialSeeds`; this restores the shared fields and
  // clears location.state.
  useEffect(() => {
    const state = location.state as { prefill?: PrefillState } | null
    if (!state?.prefill) return

    const prefill = state.prefill
    window.history.replaceState({}, '')

    if (prefill.diagnosisCode) setDiagnosisCode(prefill.diagnosisCode)
    if (
      prefill.paymentType &&
      isAllowedOption(prefill.paymentType as PaymentType, allowedPaymentTypes)
    ) {
      setPaymentType(prefill.paymentType as PaymentType)
    }
    if (
      prefill.deliveryMethod &&
      isAllowedOption(prefill.deliveryMethod as DeliveryMethod, allowedDeliveryMethods)
    ) {
      setDeliveryMethod(prefill.deliveryMethod as DeliveryMethod)
    }

    if (prefill.samePatient) {
      methods.setValue('patient_type', prefill.patientType)
      methods.setValue('no_known_allergies', prefill.noKnownAllergies || false)
      methods.setValue('current_medications', prefill.currentMedications || '')
      methods.setValue('no_current_medications', prefill.noCurrentMedications || false)
      methods.setValue('patient_gender', prefill.patientGender || 'unknown')
      methods.setValue('patient_email', prefill.patientEmail || '')

      if (prefill.patientType === 'human') {
        methods.setValue('patient_first_name', prefill.patientFirstName || '')
        methods.setValue('patient_last_name', prefill.patientLastName || '')
        methods.setValue('patient_middle_name', prefill.patientMiddleName || '')
        methods.setValue('patient_dob', prefill.patientDob || '')
        methods.setValue('patient_phone', prefill.patientPhone || '')
        methods.setValue('patient_allergies', prefill.patientAllergies || '')

        if (prefill.patientAddress) {
          methods.setValue('patient_address_street', prefill.patientAddress.street || '')
          methods.setValue('patient_address_city', prefill.patientAddress.city || '')
          methods.setValue('patient_address_state', prefill.patientAddress.state || '')
          methods.setValue('patient_address_zip', prefill.patientAddress.zip || '')
        }
      } else {
        methods.setValue('animal_name', prefill.animalName || '')
        methods.setValue('animal_species', prefill.animalSpecies || '')
        methods.setValue('animal_breed', prefill.animalBreed || '')
        methods.setValue('patient_dob', prefill.patientDob || '')
        methods.setValue('animal_dob_unknown', prefill.animalDobUnknown ?? false)
        const ownerParts = splitOwnerName(prefill.ownerName)
        methods.setValue('owner_first_name', ownerParts.first)
        methods.setValue('owner_last_name', ownerParts.last)
        methods.setValue('owner_phone', prefill.ownerPhone || '')
        methods.setValue('patient_weight', prefill.patientWeight || '')
        methods.setValue('patient_weight_unit', prefill.patientWeightUnit || 'lbs')
        methods.setValue('patient_allergies', prefill.patientAllergies || '')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Watch patient fields for completeness check
  const patientType = watch('patient_type')
  const patientFirstName = watch('patient_first_name')
  const patientLastName = watch('patient_last_name')
  const patientDob = watch('patient_dob')
  const patientPhone = watch('patient_phone')
  const patientAddressStreet = watch('patient_address_street')
  const patientAddressCity = watch('patient_address_city')
  const patientAddressState = watch('patient_address_state')
  const patientAddressZip = watch('patient_address_zip')
  const animalName = watch('animal_name')
  const animalSpecies = watch('animal_species')
  const ownerFirstName = watch('owner_first_name')
  const ownerLastName = watch('owner_last_name')
  const ownerPhone = watch('owner_phone')
  const patientWeight = watch('patient_weight')
  const animalDobUnknown = watch('animal_dob_unknown')

  const isHuman = patientType === 'human'

  // Per-provider effective settings (PORTAL-72). Prefer the authenticated
  // provider profile (which resolves per-provider overrides over the pharmacy
  // global); fall back to the public portal config, then to the safe default.
  const signatureRequired =
    user?.signature_required ?? config?.signature_required ?? false
  const patientAddressRequired =
    user?.patient_address_required ?? config?.patient_address_required ?? false

  // A required patient address (human patients only, PORTAL-72) is part of
  // patient-section completeness. Folding it in here keeps the "complete"
  // signal honest so the section can't be collapsed to a summary — hiding the
  // address fields — while the address the submit guard rejects is still empty.
  const patientAddressComplete =
    !!patientAddressStreet &&
    !!patientAddressCity &&
    !!patientAddressState &&
    !!patientAddressZip

  const isPatientComplete = isHuman
    ? !!(patientFirstName && patientLastName && patientDob && patientPhone) &&
      (!patientAddressRequired || patientAddressComplete)
    : !!(
        animalName &&
        animalSpecies &&
        ownerFirstName.trim() &&
        ownerLastName.trim() &&
        ownerPhone &&
        patientWeight &&
        (patientDob || animalDobUnknown)
      )

  // Payment & Delivery visibility (PORTAL-65). Each child flag folds in the
  // parent flag; the card is hidden when the parent hides it OR every child is
  // hidden. Defaults to "show" while config is still loading.
  const hidePaymentDeliverySection =
    user?.hide_payment_delivery_section ?? config?.hide_payment_delivery_section ?? false
  const hideDiagnosis =
    hidePaymentDeliverySection ||
    (user?.hide_diagnosis_code ?? config?.hide_diagnosis_code ?? false)
  const hidePaymentType =
    hidePaymentDeliverySection ||
    (user?.hide_payment_type ?? config?.hide_payment_type ?? false)
  const hideDeliveryMethod =
    hidePaymentDeliverySection ||
    (user?.hide_delivery_method ?? config?.hide_delivery_method ?? false)
  const hidePaymentDeliveryCard =
    hideDiagnosis && hidePaymentType && hideDeliveryMethod

  // Payment & Delivery requirements (PORTAL-202). The profile already resolves
  // a hidden field's requirement to false; the portal-config fallback carries
  // raw pharmacy values, so fold the hide flags in here too. A locked address
  // option already fills its field's state, so it satisfies the requirement.
  const diagnosisCodeRequired =
    !hideDiagnosis &&
    (user?.diagnosis_code_required ?? config?.diagnosis_code_required ?? false)
  const paymentTypeRequired =
    !hidePaymentType &&
    (user?.payment_type_required ?? config?.payment_type_required ?? false)
  const deliveryMethodRequired =
    !hideDeliveryMethod &&
    (user?.delivery_method_required ?? config?.delivery_method_required ?? false)
  const missingPaymentDeliveryFields = useMemo(
    () => [
      // Typed text only counts once picked from the search results.
      ...(diagnosisCodeRequired && !diagnosisCode.trim()
        ? ['diagnosis code (select one from the search results)']
        : []),
      ...(paymentTypeRequired && !paymentType ? ['payment type'] : []),
      ...(deliveryMethodRequired && !deliveryMethod ? ['delivery method'] : []),
    ],
    [
      diagnosisCodeRequired,
      diagnosisCode,
      paymentTypeRequired,
      paymentType,
      deliveryMethodRequired,
      deliveryMethod,
    ],
  )

  // General (pharmacy-level) custom fields (PORTAL-164): asked once per
  // order, after the product sections. Gated like product custom fields so the
  // button never greys out without an explanation.
  const generalCustomFields = useMemo(
    () => user?.general_custom_fields ?? [],
    [user?.general_custom_fields],
  )
  const generalCustomFieldValues = watch('general_custom_field_values')
  const missingGeneralCustomFields = getMissingRequiredCustomFields(
    generalCustomFields,
    generalCustomFieldValues,
  )

  const allSectionsReady = sectionIds.every((id) => readyMap[id])
  const isFormValid =
    isPatientComplete &&
    sectionIds.length > 0 &&
    allSectionsReady &&
    missingGeneralCustomFields.length === 0 &&
    missingPaymentDeliveryFields.length === 0 &&
    (!signatureRequired || !!providerSignature)

  // Patient selection (autofill from history)
  const handlePatientSelect = useCallback(
    (patient: PatientSummary | null) => {
      if (!patient) {
        methods.setValue('patient_first_name', '')
        methods.setValue('patient_last_name', '')
        methods.setValue('patient_middle_name', '')
        methods.setValue('patient_dob', '')
        methods.setValue('patient_gender', 'unknown')
        methods.setValue('patient_phone', '')
        methods.setValue('patient_email', '')
        methods.setValue('patient_address_street', '')
        methods.setValue('patient_address_city', '')
        methods.setValue('patient_address_state', '')
        methods.setValue('patient_address_zip', '')
        methods.setValue('patient_allergies', '')
        methods.setValue('no_known_allergies', false)
        methods.setValue('current_medications', '')
        methods.setValue('no_current_medications', false)
        methods.setValue('animal_name', '')
        methods.setValue('animal_species', '')
        methods.setValue('animal_breed', '')
        methods.setValue('owner_first_name', '')
        methods.setValue('owner_last_name', '')
        methods.setValue('owner_phone', '')
        methods.setValue('patient_weight', '')
        methods.setValue('patient_weight_unit', 'lbs')
        methods.setValue('animal_dob_unknown', false)
        return
      }

      methods.setValue('patient_type', patient.patient_type as 'human' | 'animal')
      methods.setValue('no_known_allergies', patient.no_known_allergies || false)
      methods.setValue('current_medications', patient.current_medications || '')
      methods.setValue('no_current_medications', patient.no_current_medications || false)
      methods.setValue('patient_gender', patient.patient_gender || 'unknown')
      methods.setValue('patient_email', patient.patient_email || '')

      if (patient.patient_type === 'human') {
        methods.setValue('patient_first_name', patient.patient_first_name || '')
        methods.setValue('patient_last_name', patient.patient_last_name || '')
        methods.setValue('patient_middle_name', patient.patient_middle_name || '')
        methods.setValue('patient_dob', patient.patient_dob || '')
        methods.setValue('patient_phone', patient.patient_phone || '')
        methods.setValue('patient_allergies', patient.patient_allergies || '')

        if (patient.patient_address) {
          methods.setValue('patient_address_street', patient.patient_address.street || '')
          methods.setValue('patient_address_city', patient.patient_address.city || '')
          methods.setValue('patient_address_state', patient.patient_address.state || '')
          methods.setValue('patient_address_zip', patient.patient_address.zip || '')
        }
      } else {
        methods.setValue('animal_name', patient.animal_name || '')
        methods.setValue('animal_species', patient.animal_species || '')
        methods.setValue('animal_breed', patient.animal_breed || '')
        methods.setValue('patient_dob', patient.patient_dob || '')
        methods.setValue('animal_dob_unknown', patient.animal_dob_unknown ?? false)
        const ownerParts = splitOwnerName(patient.owner_name)
        methods.setValue('owner_first_name', ownerParts.first)
        methods.setValue('owner_last_name', ownerParts.last)
        methods.setValue('owner_phone', patient.owner_phone || '')
        methods.setValue('patient_weight', patient.patient_weight || '')
        methods.setValue('patient_weight_unit', patient.patient_weight_unit || 'lbs')
        methods.setValue('patient_allergies', patient.patient_allergies || '')
      }
    },
    [methods]
  )

  // Section management. The form holds up to MAX_PRODUCTS_PER_SUBMISSION
  // products (PORTAL-190); on submit they are sent in requests of at most
  // BULK_MAX_LINE_ITEMS_PER_REQUEST, see submitPrescriptionBulkChunked.
  const handleAddSection = useCallback(() => {
    setSectionIds((prev) =>
      prev.length >= MAX_PRODUCTS_PER_SUBMISSION
        ? prev
        : [...prev, newSectionId()],
    )
  }, [])

  const handleRemoveSection = useCallback((id: string) => {
    setSectionIds((prev) => prev.filter((existing) => existing !== id))
    delete sectionRefs.current[id]
    setReadyMap((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }, [])

  const handleSectionReadyChange = useCallback((id: string, isReady: boolean) => {
    setReadyMap((prev) => {
      if (prev[id] === isReady) return prev
      return { ...prev, [id]: isReady }
    })
  }, [])

  // Build the shared patient payload sent once per bulk submission. Mirrors
  // backend `PrescriptionPatientPayload`. Per-product overrides come from
  // each ProductSection's getPayload().
  const buildPatientPayload = useCallback(
    (data: PrescriptionFormData): PrescriptionPatientPayload => {
      const hasAddress =
        data.patient_address_street ||
        data.patient_address_city ||
        data.patient_address_state ||
        data.patient_address_zip

      const payload: PrescriptionPatientPayload = {
        patient_type: data.patient_type,
        patient_allergies: data.patient_allergies?.trim() || null,
        no_known_allergies: data.no_known_allergies || false,
        current_medications: data.current_medications?.trim() || null,
        no_current_medications: data.no_current_medications || false,
        patient_gender: data.patient_gender,
        patient_email: data.patient_email?.trim() || null,
        diagnosis_code: hideDiagnosis ? null : diagnosisCode || null,
        payment_type: hidePaymentType ? null : (paymentType as PaymentType) || null,
        delivery_method: hideDeliveryMethod
          ? null
          : (deliveryMethod as DeliveryMethod) || null,
      }

      if (data.patient_type === 'human') {
        payload.patient_first_name = data.patient_first_name.trim()
        payload.patient_last_name = data.patient_last_name.trim()
        payload.patient_dob = data.patient_dob
        payload.patient_phone = data.patient_phone.replace(/\D/g, '')
        payload.patient_middle_name = data.patient_middle_name?.trim() || null
        payload.patient_address = hasAddress
          ? {
              street: data.patient_address_street || '',
              city: data.patient_address_city || '',
              state: data.patient_address_state || '',
              zip: buildPatientZip(pharmacyCountry, data.patient_address_zip || ''),
              country: pharmacyCountry,
            }
          : null
      } else {
        payload.animal_name = data.animal_name.trim()
        payload.animal_species = data.animal_species.trim()
        payload.animal_breed = data.animal_breed?.trim() || null
        payload.patient_dob = data.animal_dob_unknown ? null : data.patient_dob || null
        payload.animal_dob_unknown = data.animal_dob_unknown || false
        payload.owner_name =
          `${data.owner_first_name.trim()} ${data.owner_last_name.trim()}`.trim()
        payload.owner_phone = data.owner_phone.replace(/\D/g, '')
        payload.patient_weight = data.patient_weight || null
        payload.patient_weight_unit = data.patient_weight_unit || null
      }

      return payload
    },
    [
      pharmacyCountry,
      diagnosisCode,
      paymentType,
      deliveryMethod,
      hideDiagnosis,
      hidePaymentType,
      hideDeliveryMethod,
    ]
  )

  // Submit handler
  const onSubmit = useCallback(
    async (data: PrescriptionFormData) => {
      // Validate every product section
      const validations = await Promise.all(
        sectionIds.map(async (id) => {
          const ref = sectionRefs.current[id]
          return ref ? await ref.validate() : false
        })
      )
      if (validations.some((v) => !v)) return

      // Validate signature when required
      if (signatureRequired && !providerSignature) {
        setSignatureError('A provider signature is required to submit this prescription.')
        return
      }
      setSignatureError(undefined)

      // Validate patient address when required (human patients only — PORTAL-72)
      if (patientAddressRequired && data.patient_type === 'human') {
        const addressComplete =
          !!data.patient_address_street &&
          !!data.patient_address_city &&
          !!data.patient_address_state &&
          !!data.patient_address_zip
        if (!addressComplete) {
          showError('A complete patient address is required to submit this prescription.')
          return
        }
      }

      // Validate required Payment & Delivery fields (PORTAL-202)
      if (missingPaymentDeliveryFields.length > 0) {
        showError(
          `The following are required to submit this prescription: ${missingPaymentDeliveryFields.join(', ')}.`
        )
        return
      }

      // Collect per-section line items + display metadata for the confirmation page.
      const sectionMeta: { product_display_name: string }[] = []
      const lineItems: PrescriptionLineItem[] = []
      for (const id of sectionIds) {
        const ref = sectionRefs.current[id]
        if (!ref) continue
        const payload = ref.getPayload()
        const product = ref.getProduct()
        if (!payload || !product) {
          showError('Please complete every product section before submitting.')
          return
        }
        lineItems.push({
          catalog_product_id: payload.catalog_product_id,
          dose_specific_directions: payload.dose_specific_directions,
          quantity: payload.quantity,
          unit_amount: payload.unit_amount,
          days_supply: payload.days_supply,
          refills: payload.refills,
          custom_field_values: payload.custom_field_values,
        })
        sectionMeta.push({ product_display_name: product.display_name })
      }

      const bulkPayload = {
        patient: buildPatientPayload(data),
        practice_address_id: selectedAddressId || null,
        signature: providerSignature ?? null,
        prescriptions: lineItems,
        general_custom_field_values: compactCustomFieldValues(
          pickDefinedCustomFieldValues(generalCustomFields, data.general_custom_field_values),
        ),
      }

      const patientName =
        data.patient_type === 'human'
          ? `${data.patient_first_name} ${data.patient_last_name}`.trim()
          : `${data.animal_name} (${data.owner_first_name} ${data.owner_last_name})`

      // Reaching the confirmation page means at least one product was
      // confirmed, so the source draft (if any) is removed from the queue —
      // resuming it would fax those products again. Same as main's handling
      // of per-item failures: unsent products are re-entered by hand.
      // Fire-and-forget: a cleanup failure must not block the confirmation.
      const goToConfirmation = (results: BulkPrescriptionResult[]) => {
        if (draftId) {
          void deleteDraft(slug, draftId).catch(() => {})
        }
        const submittedCount = results.filter((r) => r.status === 'submitted').length
        navigate(`/rx/${slug}/confirmation/bulk`, {
          state: {
            results,
            successfulCount: submittedCount,
            failedCount: results.length - submittedCount,
            sectionMeta,
            patientName,
            submittedAt: new Date().toISOString(),
          },
        })
      }

      try {
        // Sent in chunks of BULK_MAX_LINE_ITEMS_PER_REQUEST so no single
        // request can outlive the proxy timeout (PORTAL-190).
        const response = await submitPrescriptionBulkChunked(slug, bulkPayload)
        goToConfirmation(response.results)
      } catch (err) {
        // A later chunk failed after earlier ones were confirmed. Show a
        // per-product outcome like any other partial submission: the confirmed
        // results as-is, the rest marked failed. Items after the failed chunk
        // were never sent. Items IN the failed chunk depend on why it failed:
        // on a timeout, network drop or 5xx the backend may have kept going
        // and faxed them, so they are labelled "outcome unknown" with a
        // pointer to History; on a definite rejection (4xx: pre-flight gate,
        // validation, expired session) nothing was created, so they get the
        // server's own reason like any other failed item. If the FIRST chunk
        // fails, nothing was sent and the generic handling below keeps the
        // form (and any source draft) intact, same as main.
        if (err instanceof BulkSubmitPartialError) {
          console.error('Bulk prescription submission partially failed:', err.cause)
          const sentCount = err.results.length
          const attemptedEnd = sentCount + err.attemptedCount

          const cause = err.cause
          const rejected =
            cause instanceof ApiClientError && !cause.isRetryable()
          const attemptedError = rejected
            ? `Not sent: ${cause.message}`
            : 'Outcome unknown: the request for this batch did not complete. Check History before entering it again — the pharmacy may already have received it.'

          const unsent: BulkPrescriptionResult[] = lineItems
            .slice(sentCount)
            .map((item, i) => {
              const index = sentCount + i
              return {
                index,
                catalog_product_id: item.catalog_product_id,
                status: 'failed',
                prescription_id: null,
                reference_id: null,
                error:
                  index < attemptedEnd
                    ? attemptedError
                    : 'Not sent: an earlier batch failed, so this product was never submitted.',
              }
            })

          goToConfirmation([...err.results, ...unsent])
          return
        }

        // The server's email check is stricter than the form's: show its
        // reason on the field, reopening the patient section if collapsed
        const detail = err instanceof ApiClientError ? err.data?.detail : undefined
        const emailError = Array.isArray(detail)
          ? detail.find((d) => d.loc?.at(-1) === 'patient_email')
          : undefined
        if (emailError) {
          setPatientSectionCollapsed(false)
          methods.setError('patient_email', { type: 'server', message: emailError.msg })
          return
        }
        console.error('Bulk prescription submission error:', err)
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to submit prescriptions. Please try again.'
        showError(errorMessage)
      }
    },
    [
      sectionIds,
      signatureRequired,
      patientAddressRequired,
      missingPaymentDeliveryFields,
      providerSignature,
      selectedAddressId,
      buildPatientPayload,
      slug,
      navigate,
      showError,
      draftId,
      generalCustomFields,
      methods,
    ]
  )

  // Save-for-later handler (PORTAL-61). Persists the whole form — patient,
  // every product's full values, payment/delivery, and the signature if the
  // user signed — without submitting, validating, or faxing. Updates the
  // existing draft when continuing one, otherwise creates a new draft.
  const handleSaveDraft = useCallback(async () => {
    const data = methods.getValues()

    const lineItems: DraftLineItem[] = []
    for (const id of sectionIds) {
      const ref = sectionRefs.current[id]
      if (!ref) continue
      const item = ref.getDraftPayload()
      if (item) lineItems.push(item)
    }

    // Reuse the shared patient builder, then relax the strict-submit shapes a
    // draft can't satisfy (an empty date string isn't a valid date server-side).
    const patient = buildPatientPayload(data)
    if (!patient.patient_dob) patient.patient_dob = null

    const payload: PrescriptionDraftSave = {
      patient,
      practice_address_id: selectedAddressId || null,
      signature: providerSignature ?? null,
      prescriptions: lineItems,
      general_custom_field_values: compactCustomFieldValues(
        pickDefinedCustomFieldValues(generalCustomFields, data.general_custom_field_values),
      ),
    }

    try {
      setSavingDraft(true)
      if (draftId) {
        await updateDraft(slug, draftId, payload)
      } else {
        await saveDraft(slug, payload)
      }
      showSuccess('Saved. You can finish and submit it later from the Saved tab.')
      navigate(`/rx/${slug}/saved`)
    } catch (err) {
      console.error('Save draft error:', err)
      showError(
        err instanceof Error ? err.message : 'Failed to save. Please try again.'
      )
    } finally {
      setSavingDraft(false)
    }
  }, [
    methods,
    sectionIds,
    buildPatientPayload,
    selectedAddressId,
    providerSignature,
    draftId,
    slug,
    navigate,
    showSuccess,
    showError,
    generalCustomFields,
  ])

  // Toggle patient section collapse
  const togglePatientSection = useCallback(() => {
    if (isPatientComplete) {
      setPatientSectionCollapsed((prev) => !prev)
    }
  }, [isPatientComplete])

  const patientSummary = isHuman ? (
    <>
      <span className="font-medium text-[var(--color-charcoal)]">
        {patientFirstName} {patientLastName}
      </span>
      {' · '}
      <span>DOB: {patientDob}</span>
    </>
  ) : (
    <>
      <span className="font-medium text-[var(--color-charcoal)]">
        {animalName} ({animalSpecies})
      </span>
      {' · '}
      <span>Owner: {ownerFirstName} {ownerLastName}</span>
    </>
  )

  return (
    <PageContainer size="md" className="py-8">
      <ProviderPageHeader />

      <ProviderTabBar />

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Patient Information Section */}
          <Card>
            <div className="space-y-5">
              <SectionHeader
                title="Patient Information"
                icon={<User className="h-4 w-4" />}
                isCollapsible={isPatientComplete}
                isCollapsed={patientSectionCollapsed}
                isComplete={isPatientComplete}
                onToggle={togglePatientSection}
              />

              {patientSectionCollapsed && isPatientComplete && (
                <div className="pl-11 text-sm text-[var(--color-gray-600)]">
                  {patientSummary}
                </div>
              )}

              {!patientSectionCollapsed && (
                <PatientSelector
                  onPatientSelect={handlePatientSelect}
                  disabled={isSubmitting}
                  patientType={isHuman ? 'human' : 'animal'}
                />
              )}

              {!patientSectionCollapsed && (
                <PatientForm
                  disabled={isSubmitting}
                  portalType={portalType}
                  patientAddressRequired={patientAddressRequired}
                  country={pharmacyCountry}
                />
              )}
            </div>
          </Card>

          {/* Practice Address Section (only rendered when provider has >1). */}
          {providerAddresses.length > 1 && (
            <Card>
              <div className="space-y-4">
                <SectionHeader
                  title="Prescribing From"
                  icon={<MapPin className="h-4 w-4" />}
                  isComplete={!!selectedAddressId}
                />
                <p className="text-sm text-[var(--color-gray-600)]">
                  Choose which office these prescriptions are coming from.
                </p>
                <div className="space-y-2">
                  {providerAddresses.map((address) => {
                    const checked = selectedAddressId === address.id
                    return (
                      <label
                        key={address.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          checked
                            ? 'border-[var(--pharmacy-primary)] bg-[var(--color-soft-lavender)]'
                            : 'border-[var(--color-gray-200)] hover:border-[var(--color-gray-400)]'
                        }`}
                      >
                        <input
                          type="radio"
                          name="provider_address"
                          value={address.id}
                          checked={checked}
                          onChange={() => setSelectedAddressId(address.id)}
                          disabled={isSubmitting}
                          className="mt-1 h-4 w-4 text-[var(--pharmacy-primary)] focus:ring-[var(--pharmacy-secondary)] border-gray-300"
                        />
                        <div className="min-w-0">
                          <div className="font-medium text-[var(--color-charcoal)]">
                            {address.office_name || 'Practice Address'}
                            {address.is_primary && (
                              <span className="ml-2 text-xs font-medium text-[var(--color-success)]">
                                Primary
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-[var(--color-gray-600)] break-words">
                            {formatPracticeAddressLine(address)}
                          </div>
                        </div>
                      </label>
                    )
                  })}
                </div>
              </div>
            </Card>
          )}

          {/* Product Sections (PORTAL-26 — one or more). Each initial section
              may carry a seed (prescribe-again deep link or a saved-draft
              restore); sections the user adds later have no seed. */}
          {sectionIds.map((id, idx) => {
            const seed = seedById[id]
            return (
              <ProductSection
                key={id}
                ref={(node) => {
                  sectionRefs.current[id] = node
                }}
                index={idx}
                disabled={isSubmitting}
                onRemove={idx === 0 ? undefined : () => handleRemoveSection(id)}
                onReadyChange={(isReady) => handleSectionReadyChange(id, isReady)}
                prefillProductId={seed?.productId}
                prefillCustomFieldValues={seed?.customFieldValues}
                prefillValues={seed?.values}
              />
            )
          })}

          {/* Add another product (capped at MAX_PRODUCTS_PER_SUBMISSION) */}
          <div className="flex justify-start items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={handleAddSection}
              disabled={
                isSubmitting || sectionIds.length >= MAX_PRODUCTS_PER_SUBMISSION
              }
            >
              <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
              Add another product
            </Button>
            {sectionIds.length >= MAX_PRODUCTS_PER_SUBMISSION && (
              <span className="text-xs text-[var(--color-gray-500)]">
                Maximum of {MAX_PRODUCTS_PER_SUBMISSION} prescriptions per submission.
              </span>
            )}
          </div>

          {/* General custom fields (PORTAL-164): the pharmacy's order-level
              questions, asked once after every product section. Rendered in
              the page-level FormProvider under general_custom_field_values. */}
          {generalCustomFields.length > 0 && (
            <Card>
              <div className="space-y-5">
                <SectionHeader
                  title="Additional Information"
                  icon={<ClipboardList className="h-4 w-4" />}
                  isComplete={missingGeneralCustomFields.length === 0}
                />
                <CustomFieldsEdit
                  fields={generalCustomFields}
                  disabled={isSubmitting}
                  formPrefix="general_custom_field_values"
                  bare
                />
                {missingGeneralCustomFields.length > 0 && (
                  <p className="text-sm text-[var(--color-warning)]">
                    Complete the required Additional Information before submitting:{' '}
                    {missingGeneralCustomFields.map((f) => f.label).join(', ')}.
                  </p>
                )}
              </div>
            </Card>
          )}

          {/* Payment, Delivery & Diagnosis Section.
              Hidden entirely when the pharmacy hides the whole section OR every
              element inside it is hidden (PORTAL-65); otherwise each element is
              hidden independently. */}
          {!hidePaymentDeliveryCard && (
          <Card>
            <div className="space-y-5">
              <SectionHeader
                title="Payment & Delivery"
                icon={<CreditCard className="h-4 w-4" />}
              />

              {!hideDiagnosis && (
                <DiagnosisCodeSearch
                  value={diagnosisCode}
                  onChange={setDiagnosisCode}
                  disabled={isSubmitting}
                  required={diagnosisCodeRequired}
                />
              )}

              {!hidePaymentType && (
              <div>
                <label className="block text-sm font-medium text-[var(--color-charcoal)] mb-2">
                  Payment Type
                  {paymentTypeRequired && (
                    <span className="text-error ml-0.5" aria-hidden="true">
                      *
                    </span>
                  )}
                </label>
                {lockedPaymentType ? (
                  <div className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[var(--color-soft-lavender)] border border-[var(--color-gray-200)]">
                    <span className="text-sm font-medium text-[var(--color-charcoal)]">
                      {lockedPaymentType === 'patient_pay'
                        ? 'Patient Pay'
                        : providerPayLabel(config?.use_clinic_pay_label)}
                    </span>
                    <span className="text-xs text-[var(--color-gray-500)]">
                      (set by pharmacy)
                    </span>
                  </div>
                ) : (
                  <div className="flex gap-4">
                    {isAllowedOption('patient_pay', allowedPaymentTypes) && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="payment_type"
                        value="patient_pay"
                        checked={paymentType === 'patient_pay'}
                        onChange={(e) => setPaymentType(e.target.value as PaymentType)}
                        disabled={isSubmitting}
                        className="h-4 w-4 text-[var(--pharmacy-primary)] focus:ring-[var(--pharmacy-secondary)] border-gray-300"
                      />
                      <span className="text-sm text-[var(--color-charcoal)]">Patient Pay</span>
                    </label>
                    )}
                    {isAllowedOption('provider_pay', allowedPaymentTypes) && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="payment_type"
                        value="provider_pay"
                        checked={paymentType === 'provider_pay'}
                        onChange={(e) => setPaymentType(e.target.value as PaymentType)}
                        disabled={isSubmitting}
                        className="h-4 w-4 text-[var(--pharmacy-primary)] focus:ring-[var(--pharmacy-secondary)] border-gray-300"
                      />
                      <span className="text-sm text-[var(--color-charcoal)]">
                        {providerPayLabel(config?.use_clinic_pay_label)}
                      </span>
                    </label>
                    )}
                    {paymentType && (
                      <button
                        type="button"
                        onClick={() => setPaymentType('')}
                        className="text-xs text-[var(--color-gray-500)] hover:text-[var(--color-charcoal)] underline"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}
              </div>
              )}

              {!hideDeliveryMethod && (
              <div>
                <label className="block text-sm font-medium text-[var(--color-charcoal)] mb-2">
                  Delivery Method
                  {deliveryMethodRequired && (
                    <span className="text-error ml-0.5" aria-hidden="true">
                      *
                    </span>
                  )}
                </label>
                {lockedDeliveryMethod ? (
                  <div className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[var(--color-soft-lavender)] border border-[var(--color-gray-200)]">
                    <span className="text-sm font-medium text-[var(--color-charcoal)]">
                      {DELIVERY_METHOD_LABELS[lockedDeliveryMethod]}
                    </span>
                    <span className="text-xs text-[var(--color-gray-500)]">
                      (set by pharmacy)
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {DELIVERY_METHOD_OPTIONS.filter((option) =>
                        isAllowedOption(option.value, allowedDeliveryMethods)
                      ).map((option) => (
                        <label
                          key={option.value}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="delivery_method"
                            value={option.value}
                            checked={deliveryMethod === option.value}
                            onChange={(e) => setDeliveryMethod(e.target.value as DeliveryMethod)}
                            disabled={isSubmitting}
                            className="h-4 w-4 text-[var(--pharmacy-primary)] focus:ring-[var(--pharmacy-secondary)] border-gray-300"
                          />
                          <span className="text-sm text-[var(--color-charcoal)]">{option.label}</span>
                        </label>
                      ))}
                    </div>
                    {deliveryMethod && (
                      <button
                        type="button"
                        onClick={() => setDeliveryMethod('')}
                        className="mt-1 text-xs text-[var(--color-gray-500)] hover:text-[var(--color-charcoal)] underline"
                      >
                        Clear
                      </button>
                    )}
                  </>
                )}
              </div>
              )}

              {missingPaymentDeliveryFields.length > 0 && (
                <p className="text-sm text-[var(--color-warning)]">
                  Complete the required Payment &amp; Delivery fields before submitting:{' '}
                  {missingPaymentDeliveryFields.join(', ')}.
                </p>
              )}
            </div>
          </Card>
          )}

          {/* Electronic Signature Section. Always shown; the effective
              per-provider setting (PORTAL-72) only decides whether signing is
              required to submit (PORTAL-158). */}
          <Card>
            <div className="space-y-5">
              <SectionHeader
                title={signatureRequired ? 'Provider Signature' : 'Provider Signature (optional)'}
                icon={<PenLine className="h-4 w-4" />}
                isComplete={!!providerSignature}
              />
              <SignatureCapture
                initialValue={initialDraftSignature}
                onChange={(sig) => {
                  setProviderSignature(sig)
                  if (sig) setSignatureError(undefined)
                }}
                required={signatureRequired}
                disabled={isSubmitting}
                error={signatureError}
              />
            </div>
          </Card>

          {/* Form validation errors summary (patient fields only) */}
          {Object.keys(errors).length > 0 && (
            <div className="p-4 bg-[var(--color-error-light)] border border-[var(--color-error)] rounded-lg">
              <p className="text-sm font-medium text-[var(--color-error)]">
                Please fix the following errors:
              </p>
              <ul className="mt-2 text-sm text-[var(--color-error)] list-disc list-inside">
                {errors.patient_first_name && (
                  <li>{errors.patient_first_name.message}</li>
                )}
                {errors.patient_last_name && (
                  <li>{errors.patient_last_name.message}</li>
                )}
                {errors.patient_dob && <li>{errors.patient_dob.message}</li>}
                {errors.patient_phone && (
                  <li>{errors.patient_phone.message}</li>
                )}
                {errors.animal_name && (
                  <li>{errors.animal_name.message}</li>
                )}
                {errors.animal_species && (
                  <li>{errors.animal_species.message}</li>
                )}
                {errors.owner_first_name && (
                  <li>{errors.owner_first_name.message}</li>
                )}
                {errors.owner_last_name && (
                  <li>{errors.owner_last_name.message}</li>
                )}
                {errors.owner_phone && (
                  <li>{errors.owner_phone.message}</li>
                )}
                {errors.patient_email && (
                  <li>{errors.patient_email.message}</li>
                )}
                {errors.patient_weight && (
                  <li>{errors.patient_weight.message}</li>
                )}
              </ul>
            </div>
          )}

          {/* Actions: Save for later (no signature/validation needed) + Submit */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              size="lg"
              onClick={handleSaveDraft}
              disabled={isSubmitting || savingDraft}
              className="sm:min-w-[160px]"
            >
              {savingDraft ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" aria-hidden="true" />
                  {draftId ? 'Update saved script' : 'Save for later'}
                </>
              )}
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={isSubmitting || savingDraft || !isFormValid}
              className="min-w-[200px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2
                    className="h-5 w-5 mr-2 animate-spin"
                    aria-hidden="true"
                  />
                  Submitting...
                </>
              ) : sectionIds.length > 1 ? (
                `Submit ${sectionIds.length} Prescriptions`
              ) : (
                'Submit Prescription'
              )}
            </Button>
          </div>
        </form>
      </FormProvider>
    </PageContainer>
  )
}

export default PrescriptionForm
